import { effectScope } from 'vue';
import store from '@/store';
import { isDemoMode, sensitiveSetFilterParams } from '@/utils/demoSpoof';
import { installDemoSearchInput, presenterTyped } from '@/utils/demoSearchInput';

// ag-grid column filters on identity columns, for every grid.
//
// A filter keeps the term the user typed in its floating filter and popup
// inputs, outside the tagged cells, and keeps applying it. A column counts as
// an identity column when
//   - its colDef tags it (cellClass / cellClassRules 'sensitive'), or opts in
//     with `context: { demoSensitive: true }` (for a renderer that draws the
//     identity without a tag, or tags only some rows), or
//   - any rendered cell of it is `.sensitive` or contains a `.sensitive`
//     element (a cellRenderer that tags its own output). Cells are checked
//     when a filter opens, on the Demo Mode sweep, whenever a filter is
//     applied and after rows render; once seen, the column stays an identity
//     column for the grid's lifetime.
// For every identity column:
//   - every filter model is removed when Demo Mode starts, so no term from
//     before the demo is applied or shown. Set-filter selections on columns
//     that spread sensitiveSetFilterParams stay: their list and summary are
//     already masked.
//   - while it stays on, a model that appears later (the grid mounts with a
//     saved state, code applies one) is removed too, unless it is a text
//     filter whose every term the presenter typed during this demo
//     (utils/demoSearchInput). Date and number filters on identity columns
//     (a DOB) are always removed.
//   - floating-filter and popup text inputs are tagged `sensitive` and marked
//     `data-demo-search`: they stay editable, with the browser's autocomplete
//     off. Set-filter value lists without the formatter are masked and inert.
//
// With Demo Mode off the layer only keeps its registry (which grids exist,
// which filter popups are open) so entry can find them: no observers, no cell
// scans, no tags. Entry scans and tags every registered grid and open popup;
// exit disconnects the observers and removes the tags.
//
// Grids register through global grid-option callbacks (see
// demoFilterGridOptions). A grid's own onX handler, including a Vue
// @grid-ready / @filter-changed listener (ag-grid-vue passes those as grid
// options), replaces the global one for that grid, so many events register;
// the behavior itself runs from api listeners, which nothing replaces.

const TAG = 'data-demo-grid-filter';
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox', 'radio', 'button', 'submit', 'reset', 'hidden', 'file', 'image',
  'range', 'color',
]);

function hasClass(value, name) {
  return typeof value === 'string' && value.split(/\s+/).includes(name);
}

// Mirrors isSensitiveColumn in External/table/DataTable.vue, where
// `sensitive: true` plays the part of context.demoSensitive. A cellClass
// function is asked about an empty row; one that throws counts as sensitive.
export function isSensitiveColDef(colDef, common = {}) {
  if (!colDef) return false;
  if (colDef.context?.demoSensitive === true) return true;
  const rules = colDef.cellClassRules;
  if (rules && typeof rules === 'object') {
    if (Object.keys(rules).some((key) => hasClass(key, 'sensitive'))) return true;
  }
  let cls = colDef.cellClass;
  if (typeof cls === 'function') {
    try {
      cls = cls({ ...common, colDef, value: undefined, data: {}, node: null, rowIndex: null });
    } catch {
      return true;
    }
  }
  if (Array.isArray(cls)) cls = cls.join(' ');
  return hasClass(cls, 'sensitive');
}

function isMaskedSetFilter(colDef) {
  const formatter = colDef?.filterParams?.valueFormatter;
  return !!formatter && formatter === sensitiveSetFilterParams.valueFormatter;
}

// The text terms of a text filter model (one condition, or several joined by
// AND/OR), or null for any other filter type.
function textTerms(model) {
  if (model?.filterType !== 'text') return null;
  const conditions = Array.isArray(model.conditions) ? model.conditions : [model];
  return conditions.map((c) => c?.filter).filter((t) => t != null && t !== '');
}

// Whether a filter model on a sensitive column may stay applied while
// protected: a set selection whose every surface is already masked, or (after
// Demo Mode has started) a text filter whose every term the presenter typed
// during this demo. Text filters are cleared unconditionally when Demo Mode
// starts (`onEntry`), so a term from before the demo never survives.
export function keepsFilterModel(colDef, model, { onEntry = false } = {}) {
  if (model == null) return true;
  if (model.filterType === 'set' && isMaskedSetFilter(colDef)) return true;
  if (onEntry) return false;
  const terms = textTerms(model);
  return !!terms && terms.length > 0 && presenterTyped(terms);
}

function alive(api) {
  return !!api && !api.isDestroyed?.();
}

// Primary columns plus the grid's own (auto group) columns, once each.
function allColumns(api) {
  const byId = new Map();
  for (const col of [...(api.getColumns?.() || []), ...(api.getAllGridColumns?.() || [])]) {
    if (col && !byId.has(col.getColId())) byId.set(col.getColId(), col);
  }
  return [...byId.values()];
}

const sanitisedId = (col) => col.colIdSanitised ?? col.getColId();

// Records every column with a rendered cell that carries the tag, and returns
// whether a column was added. Cells of a nested (detail) grid belong to it.
export function scanRenderedCells(api) {
  const entry = entries.get(api);
  if (!entry || !alive(api)) return false;
  const roots = gridRoots(api);
  if (!roots.length) return false;
  let bySanitised = null;
  let added = false;
  for (const root of roots) {
    const hits = root.querySelectorAll('.ag-cell.sensitive[col-id], .ag-cell[col-id] .sensitive');
    for (const hit of hits) {
      const cell = hit.matches('.ag-cell[col-id]') ? hit : hit.closest('.ag-cell[col-id]');
      if (!cell || cell.closest('[grid-id]') !== root) continue;
      bySanitised ??= new Map(allColumns(api).map((col) => [sanitisedId(col), col.getColId()]));
      const id = bySanitised.get(cell.getAttribute('col-id'));
      if (id == null || entry.renderedSensitive.has(id)) continue;
      entry.renderedSensitive.add(id);
      added = true;
    }
  }
  return added;
}

function isSensitiveColumn(api, col, common) {
  if (entries.get(api)?.renderedSensitive.has(col.getColId())) return true;
  return isSensitiveColDef(col.getColDef(), common);
}

function sensitiveColumns(api) {
  const common = { api, context: api.getGridOption?.('context') };
  return allColumns(api).filter((col) => isSensitiveColumn(api, col, common));
}

// Removes the filter models protected columns must not keep. Returns the ids
// of the columns it cleared.
export function stripSensitiveFilters(api, { onEntry = false } = {}) {
  if (!alive(api)) return [];
  scanRenderedCells(api);
  let model;
  try {
    model = api.getFilterModel() || {};
  } catch {
    return [];
  }
  const cleared = [];
  for (const col of sensitiveColumns(api)) {
    const id = col.getColId();
    if (!keepsFilterModel(col.getColDef(), model[id], { onEntry })) cleared.push(id);
  }
  if (!cleared.length) return cleared;
  // Clearing a column's model also resets its filter UI, so the term leaves
  // the inputs. Other columns' filters are left alone.
  Promise.all(cleared.map((id) => api.setColumnFilterModel(id, null)))
    .then(() => {
      if (alive(api)) api.onFilterChanged('api');
    })
    .catch(() => {});
  return cleared;
}

function isTextControl(el) {
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !NON_TEXT_INPUT_TYPES.has((el.getAttribute('type') || 'text').toLowerCase());
}

// Every element the layer tagged while Demo Mode was on, attached or not (a
// closed filter popup keeps its element for the next open), so exit can
// remove every tag.
const tagged = new Set();
const protectedByLayer = new WeakSet();

function tag(el, { search = false } = {}) {
  if (!el.classList.contains('sensitive')) {
    el.classList.add('sensitive');
    el.setAttribute(TAG, '');
    tagged.add(el);
  }
  // Filter text boxes stay editable: the presenter can type, and only what
  // they typed is kept (see keepsFilterModel). The engine turns off the
  // browser's autocomplete on them.
  if (!el.hasAttribute(TAG)) return;
  if (search && !el.hasAttribute('data-demo-search')) el.setAttribute('data-demo-search', '');
  else if (!search && el.hasAttribute('data-demo-search')) el.removeAttribute('data-demo-search');
}

function untag(el) {
  if (protectedByLayer.has(el)) {
    protectedByLayer.delete(el);
    el.removeAttribute('data-demo-protected');
  }
  if (!el.hasAttribute(TAG)) return;
  el.classList.remove('sensitive');
  el.removeAttribute(TAG);
  el.removeAttribute('data-demo-search');
}

function untagAll() {
  for (const el of tagged) untag(el);
  tagged.clear();
}

// A set-filter value list without the masking formatter carries raw values
// in its text, aria labels and truncation tooltips: mask the text and make it
// inert (the protected-control style blanks it and stops hover and clicks).
function protectList(list) {
  tag(list);
  if (!list.hasAttribute('data-demo-protected')) {
    list.setAttribute('data-demo-protected', '');
    protectedByLayer.add(list);
  }
}

// A filter box the presenter can type a term into. The set filter's floating
// summary (a disabled display of the selected values) and number/date inputs
// are not: they stay masked and read-only.
const SEARCH_INPUT_TYPES = new Set(['', 'text', 'search']);
function isSearchableInput(el) {
  if (el.disabled) return false;
  if (el.readOnly && !el.hasAttribute('data-demo-protected')) return false;
  return el.tagName === 'INPUT' && SEARCH_INPUT_TYPES.has((el.getAttribute('type') || '').toLowerCase());
}

function tagInputs(root, on) {
  for (const el of root.querySelectorAll('input, textarea')) {
    if (!isTextControl(el)) continue;
    if (on) tag(el, { search: isSearchableInput(el) });
    else untag(el);
  }
}

function gridRoots(api) {
  const id = api.getGridId?.();
  if (id == null || typeof document === 'undefined') return [];
  const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(String(id)) : String(id);
  return [...document.querySelectorAll(`[grid-id="${escaped}"]`)];
}

function sanitisedIds(columns) {
  return new Set(columns.map(sanitisedId));
}

export function tagFloatingFilters(api) {
  if (!alive(api)) return;
  const ids = sanitisedIds(sensitiveColumns(api));
  for (const root of gridRoots(api)) {
    for (const cell of root.querySelectorAll('.ag-floating-filter[col-id]')) {
      tagInputs(cell, ids.has(cell.getAttribute('col-id')));
    }
  }
}

// A filter popup (header button, floating-filter button, column menu). Its
// content can change after it opens (a second condition row, a set list that
// fills in), so while Demo Mode is on it is watched until it closes. With
// Demo Mode off an open popup is only recorded, so entry can protect it.
//
// ag-grid announces a popup (filterOpened) a task after it is in the page, so
// while Demo Mode is on a set-filter list is masked the moment it is added to
// a popup, before it can paint, and released once filterOpened shows that its
// column does not need it (not an identity column, or a masked set filter).
const openPopups = new Set(); // { api, column, eGui, context, observer }
const pendingLists = new Set();
let popupWatch = null;

function maskAddedSetLists(mutations) {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== 1) continue;
      const lists = node.classList.contains('ag-set-filter-list')
        ? [node]
        : node.getElementsByClassName('ag-set-filter-list');
      for (const list of lists) {
        if (list.hasAttribute(TAG) || !list.closest('.ag-popup')) continue;
        protectList(list);
        pendingLists.add(list);
      }
    }
  }
}

function startPopupWatch() {
  if (popupWatch || typeof MutationObserver === 'undefined' || !document.body) return;
  popupWatch = new MutationObserver(maskAddedSetLists);
  popupWatch.observe(document.body, { childList: true, subtree: true });
}

function stopPopupWatch() {
  popupWatch?.disconnect();
  popupWatch = null;
  pendingLists.clear();
}

function releasePendingLists(root) {
  for (const list of root.querySelectorAll('.ag-set-filter-list')) {
    if (!pendingLists.delete(list)) continue;
    untag(list);
  }
}

function dropPopup(record) {
  record.observer?.disconnect();
  record.observer = null;
  openPopups.delete(record);
}

// Popups ag-grid has closed (their element left the document).
function prunePopups({ api = null, column = null } = {}) {
  for (const record of [...openPopups]) {
    if (api && record.api !== api) continue;
    if (column && record.column !== column) continue;
    if (!alive(record.api) || !record.eGui.isConnected) dropPopup(record);
  }
}

function protectPopup(record) {
  const { api, column, eGui } = record;
  if (scanRenderedCells(api)) refresh(api);
  const sensitive = isSensitiveColumn(api, column, { api, context: record.context });
  const maskList = sensitive && !isMaskedSetFilter(column.getColDef());
  if (!maskList) releasePendingLists(eGui);
  if (!sensitive) return;
  const apply = () => {
    tagInputs(eGui, true);
    if (!maskList) {
      releasePendingLists(eGui);
      return;
    }
    for (const list of eGui.querySelectorAll('.ag-set-filter-list')) {
      protectList(list);
      pendingLists.delete(list);
    }
  };
  apply();
  if (record.observer || typeof MutationObserver === 'undefined') return;
  record.observer = new MutationObserver(apply);
  record.observer.observe(eGui, { childList: true, subtree: true });
}

function onFilterOpened(event) {
  const { api, column, eGui } = event;
  if (!column || !eGui?.querySelectorAll) return;
  prunePopups();
  let record = [...openPopups].find((r) => r.eGui === eGui);
  if (!record) {
    record = { api, column, eGui, context: event.context, observer: null };
    openPopups.add(record);
  }
  if (isDemoMode()) protectPopup(record);
}

// --- Registry ---

const grids = new Set(); // WeakRef<GridApi>
const entries = new WeakMap(); // GridApi -> { renderedSensitive, headerObserver, ... }
let watchStop = null;

function forEachGrid(fn) {
  for (const ref of [...grids]) {
    const api = ref.deref();
    if (!alive(api)) {
      grids.delete(ref);
      continue;
    }
    try {
      fn(api);
    } catch {
      /* a grid mid-teardown */
    }
  }
}

function protect(api) {
  if (isDemoMode()) stripSensitiveFilters(api);
}

function disconnectHeader(entry) {
  entry.headerObserver?.disconnect();
  entry.headerObserver = null;
  entry.header = null;
}

function observeHeader(api) {
  const entry = entries.get(api);
  if (!entry || typeof MutationObserver === 'undefined') return;
  const header = gridRoots(api)[0]?.querySelector('.ag-header');
  if (!header || entry.header === header) return;
  disconnectHeader(entry);
  entry.header = header;
  // Floating filters render with the header and again as columns scroll into
  // view, sometimes after the event that announced them.
  entry.headerObserver = new MutationObserver(() => {
    if (alive(api) && isDemoMode()) tagFloatingFilters(api);
  });
  // Also attribute changes: ag-grid renders a text filter input disabled and
  // enables it once the filter is ready, and only then is it a search box.
  entry.headerObserver.observe(header, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'readonly'],
  });
}

function refresh(api) {
  if (!isDemoMode()) return;
  tagFloatingFilters(api);
  observeHeader(api);
}

// Rows render after the events that announce them (a Vue cell renderer, an
// animation frame), so while Demo Mode is on the cells are checked once things
// settle. A column found this way is protected at once: its floating filter is
// tagged and its term dropped.
function scheduleScan(api) {
  const entry = entries.get(api);
  if (!entry || entry.scanPending || !isDemoMode()) return;
  entry.scanPending = true;
  const run = () => {
    entry.scanPending = false;
    if (!alive(api) || !isDemoMode() || !scanRenderedCells(api)) return;
    refresh(api);
    protect(api);
  };
  const frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 16);
  frame(() => setTimeout(run, 0));
}

function unregister(api) {
  const entry = entries.get(api);
  if (entry) disconnectHeader(entry);
  for (const record of [...openPopups]) if (record.api === api) dropPopup(record);
  for (const ref of [...grids]) if (ref.deref() === api) grids.delete(ref);
}

const HANDLERS = {
  filterChanged: (e) => protect(e.api),
  filterOpened: (e) => onFilterOpened(e),
  // Internal ag-grid event (no eGui): the popup for this column was destroyed.
  filterClosed: (e) => prunePopups({ api: e.api, column: e.column }),
  newColumnsLoaded: (e) => {
    refresh(e.api);
    protect(e.api);
  },
  displayedColumnsChanged: (e) => refresh(e.api),
  modelUpdated: (e) => scheduleScan(e.api),
  firstDataRendered: (e) => scheduleScan(e.api),
  virtualColumnsChanged: (e) => scheduleScan(e.api),
  viewportChanged: (e) => scheduleScan(e.api),
  gridPreDestroyed: (e) => unregister(e.api),
};

function onDemoModeChanged(on) {
  prunePopups();
  if (on) {
    installDemoSearchInput();
    startPopupWatch();
    forEachGrid((api) => {
      stripSensitiveFilters(api, { onEntry: true }); // checks the rendered cells first
      refresh(api);
      // A filter or column menu open at entry was rendered with real values;
      // close it rather than mask it in place.
      api.hidePopupMenu();
    });
    prunePopups();
    for (const record of [...openPopups]) protectPopup(record);
    return;
  }
  stopPopupWatch();
  forEachGrid((api) => {
    const entry = entries.get(api);
    if (entry) disconnectHeader(entry);
  });
  for (const record of openPopups) {
    record.observer?.disconnect();
    record.observer = null;
  }
  untagAll();
}

// Returns true the first time a grid is seen.
export function registerDemoGrid(api) {
  if (!alive(api) || entries.has(api)) return false;
  entries.set(api, { renderedSensitive: new Set() });
  grids.add(new WeakRef(api));
  for (const [type, handler] of Object.entries(HANDLERS)) {
    api.addEventListener(type, handler);
  }
  if (!watchStop) {
    // The first registration can happen inside a grid component's lifecycle;
    // a detached scope keeps the watcher alive after that component unmounts,
    // for every grid of the session.
    watchStop = effectScope(true).run(() =>
      store.watch((state, getters) => getters['demoMode/isDemoMode'], onDemoModeChanged)
    );
  }
  if (isDemoMode()) {
    installDemoSearchInput();
    startPopupWatch();
    refresh(api);
    protect(api);
    scheduleScan(api);
  }
  return true;
}

function onGridEvent(event) {
  const api = event?.api;
  if (!registerDemoGrid(api)) return;
  // The api listeners were attached during this event, so they missed it.
  HANDLERS[event.type]?.(event);
}

// Spread into the global grid options. Each is only a registration hook.
export const demoFilterGridOptions = Object.fromEntries(
  [
    'onGridReady',
    'onFirstDataRendered',
    'onRowDataUpdated',
    'onModelUpdated',
    'onNewColumnsLoaded',
    'onGridColumnsChanged',
    'onDisplayedColumnsChanged',
    'onVirtualColumnsChanged',
    'onGridSizeChanged',
    'onFilterOpened',
    'onFilterModified',
    'onFilterChanged',
  ].map((key) => [key, onGridEvent])
);
